import { map, nthArg } from 'ramda';

const RESULT_KEY = Symbol('RESULT_KEY');
const NO_RESULT = Symbol('NO_RESULT');

const createNewCache = () => new Map();

const saveValueToCache = (cache, key, value) => {
  cache.set(key, value);
};

const getValueFromCache = (cache, key) => {
  if (!cache.has(key)) {
    return NO_RESULT;
  }
  return cache.get(key);
};

const deleteValueFromCache = (cache, key) => {
  cache.delete(key);
};

const getResult = (cache, args, getKey) => {
  if (args.length === 0) {
    return getValueFromCache(cache, RESULT_KEY);
  }

  const [firstArg, ...otherArgs] = args;

  const nextCache = getValueFromCache(cache, getKey(firstArg));

  return nextCache === NO_RESULT ? NO_RESULT : getResult(nextCache, otherArgs, getKey);
};

const saveResult = (result, cache, args, getKey) => {
  if (args.length === 0) {
    saveValueToCache(cache, RESULT_KEY, result);
    return;
  }

  const [firstArg, ...otherArgs] = args;

  let nextCache = getValueFromCache(cache, getKey(firstArg));
  if (nextCache === NO_RESULT) {
    nextCache = createNewCache();
    saveValueToCache(cache, getKey(firstArg), nextCache);
  }

  saveResult(result, nextCache, otherArgs, getKey);
};

const removeResult = (cache, args, getKey) => {
  if (args.length === 0) {
    deleteValueFromCache(cache, RESULT_KEY);
    return;
  }

  const [firstArg, ...otherArgs] = args;

  const nextCache = getValueFromCache(cache, getKey(firstArg));
  if (nextCache !== NO_RESULT) {
    removeResult(nextCache, otherArgs, getKey);
  }
};

export const memoize = (func: Function, getKey: Function = nthArg(0)) => {
  const cache = createNewCache();

  return (...args) => {
    let result = getResult(cache, args, getKey);

    if (result === NO_RESULT) {
      result = func(...args);
      saveResult(result, cache, args, getKey);
    }

    return result;
  };
};

export const memoizePromise = (func, getKey = nthArg(0)) => {
  const cache = createNewCache();

  return (...args) => {
    let result = getResult(cache, args, getKey);

    if (result === NO_RESULT) {
      result = func(...args);
      saveResult(result, cache, args, getKey);

      result.catch((reason) => {
        removeResult(cache, args, getKey);
        return Promise.reject(reason);
      });
    }

    return result;
  };
};

/**
 * const f1 = (...args) => console.log('f1', ...args);
 * const f2 = (...args) => console.log('f2', ...args);
 *
 * const memFns1 = memoizedOver(f1, f2);
 * const memFns2 = memoizedOver(f1, f2);
 * memFns1 === memFns2; // true
 *
 * memFns1('one', 'two');
 * // 'f1', 'one', 'two'
 * // 'f2', 'one', 'two'
 */
export const memoizedOver = memoize(
  (...functions) =>
    (...args) =>
      map((func) => func && func(...args), functions),
);
